import React, { Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { RouteErrorBoundary } from "@/components/routing/RouteErrorBoundary";
import { LoadingState } from "@/components/layout/LoadingState";
import { publicRoutes } from "@/routes/publicRoutes";
import { privateRoutes } from "@/routes/privateRoutes";
import { supportRoutes } from "@/routes/routeGroups/supportRoutes";
import { authRoutes } from "@/routes/routeGroups/authRoutes";
import { knowledgeRoutes } from "@/routes/routeGroups/knowledgeRoutes";
import { mainRoutes, secondaryRoutes } from "@/routes/mainRoutes";
import { enterpriseRoutes } from "@/routes/routeGroups/enterpriseRoutes";
import { ExtendedRouteObject } from "@/types/routes";

const NotFound = React.lazy(() => import("@/pages/NotFound"));
const PrivacyPolicy = React.lazy(() => import("@/pages/legal/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("@/pages/legal/TermsOfService"));
const HipaaNotice = React.lazy(() => import("@/pages/legal/HipaaNotice"));

export const AppRoutes = () => {
  const renderRouteElement = (Component: React.ComponentType | React.ReactElement) => {
    if (React.isValidElement(Component)) {
      return Component;
    }
    return React.createElement(Component as React.ComponentType);
  };

  // Combine all routes with priority handling
  const allRoutes = [
    ...mainRoutes,
    ...secondaryRoutes,
    ...publicRoutes,
    ...supportRoutes,
    ...authRoutes,
    ...knowledgeRoutes,
    ...privateRoutes,
    ...enterpriseRoutes
  ] as ExtendedRouteObject[];

  // Remove duplicate routes by path
  const uniqueRoutes = allRoutes.reduce((acc, current) => {
    const existingRoute = acc.find(item => item.path === current.path);
    if (!existingRoute) {
      return acc.concat([current]);
    }
    // If the current route has higher priority, replace the existing one
    if ((current.priority || 0) > ((existingRoute as ExtendedRouteObject).priority || 0)) {
      return [...acc.filter(item => item.path !== current.path), current];
    }
    return acc;
  }, [] as ExtendedRouteObject[]);

  return (
    <Routes>
      {/* Legal Routes */}
      <Route
        path="/legal/privacy-policy"
        element={
          <Suspense fallback={<LoadingState />}>
            <PrivacyPolicy />
          </Suspense>
        }
      />
      <Route
        path="/legal/terms-of-service"
        element={
          <Suspense fallback={<LoadingState />}>
            <TermsOfService />
          </Suspense>
        }
      />
      <Route
        path="/legal/hipaa-notice"
        element={
          <Suspense fallback={<LoadingState />}>
            <HipaaNotice />
          </Suspense>
        }
      />

      {/* All other routes */}
      {uniqueRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <Suspense fallback={<LoadingState />}>
              {renderRouteElement(route.element as React.ComponentType | React.ReactElement)}
            </Suspense>
          }
          errorElement={<RouteErrorBoundary />}
        />
      ))}

      {/* 404 Route */}
      <Route 
        path="*" 
        element={
          <Suspense fallback={<LoadingState />}>
            <NotFound />
          </Suspense>
        } 
      />
    </Routes>
  );
};